import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import {
  Button,
  Table,
  Spin,
  DatePicker,
  Select
} from 'antd';
import {
  notificationsAtom
} from '../../../../../atoms/Atoms';
import Card from '../../../../shared-components/container/Card';
import {
  getDatePresets,
  thousand,
  toCurrency,
  getCloserSummaryTableColumns,
  getSetterSummaryTableColumns
} from '../../../../../functions';
import api from '../../../../../api/api';

function OrgSettersSummaryTable({ orgCode, useGlobal }) {
  const referralCode = orgCode;
  const { RangePicker } = DatePicker;
  const { Option } = Select;
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState('');

  function getSummary(page) {
    if (!dateRange.start || !dateRange.end) {
      return;
    }
    setLoading(true);
    setCurrentPage(page);
    const startDate = dayjs(dateRange.start);
    const endDate = dayjs(dateRange.end);
    const startString = startDate.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(4, 'hour').add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    let url = `reports/summary/setters?startDate=${startString}&endDate=${endString}`;
    if (referralCode) {
      url += `&orgReferralCode=${referralCode}`;
    }
    url += `&orgId=${selectedOrganization}`;
    api.get(url)
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setSummary(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  const onTableChange = (page, size) => {
    if (page - 1 !== currentPage) {
      setCurrentPage(page - 1);
      getSummary(page - 1);
    } else {
      setPageSize(size);
    }
  };

  function getOrganizations() {
    setLoadingOrganizations(true);
    api.get('organizations/active')
      .then((response) => {
        setLoadingOrganizations(false);
        if (response?.data?.success) {
          setOrganizations(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoadingOrganizations(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      })
  }

  useEffect(() => {
    setDateRange({
      start: dayjs().startOf('week').format('MMM DD, YYYY'),
      end: dayjs().endOf('day').format('MMM DD, YYYY')
    });
    if (useGlobal) {
      getOrganizations();
    }
  }, []);

  useEffect(() => {
    getSummary(0);
  }, [dateRange, selectedOrganization]);

  useEffect(() => {
    getSummary(0);
  }, [pageSize]);

  return (
    <div>
      <div className="flex space-x-2 mb-2">
        {useGlobal && (
          <Select
            showSearch
            style={{ width: 200 }}
            className="ml-auto h-12"
            filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
            value={selectedOrganization}
            loading={loadingOrganizations}
            disabled={loading}
            onChange={(obj) => setSelectedOrganization(obj)}
          >
            <Option value="">{t('any_organization')}</Option>
            <Option value="-1">{t('no_organization')}</Option>
            {_.map(organizations, (c) => (
              <Option value={c.id}>{c.nameEn}</Option>
            ))}
          </Select>
        )}
        <RangePicker
          className="bg-transparent ml-auto"
          presets={getDatePresets(t)}
          disabled={loading}
          value={[
            dayjs(dateRange?.start),
            dayjs(dateRange?.end)
          ]}
          onChange={(obj) => {
            setDateRange({
              start: obj[0],
              end: obj[1]
            });
          }}
          format="MMM DD, YYYY"
        />
        <Button type="primary" loading={loading} onClick={() => getSummary(currentPage)}>{t('reload')}</Button>
      </div>
      <div className="grid gap-4 my-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        <Spin spinning={loading}>
          <Card
            className="col-span-1 w-full border-4 rounded-sm"
            title={t('setter_leads')}
            description={thousand(summary?.totalLeads)}
          />
        </Spin>
        <Spin spinning={loading}>
          <Card
            className="col-span-1 w-full border-4 rounded-sm"
            title={t('referrals_by_customer')}
            description={thousand(summary?.totalReferralsByCustomer)}
          />
        </Spin>
      </div>
      <Table
        rowKey="userId"
        locale={{
          emptyText: t('no_data')
        }}
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage + 1,
          total: summary?.totalUsers,
          onChange: onTableChange
        }}
        bordered
        loading={loading}
        dataSource={summary?.summary}
        columns={[
          {
            title: t('setter'),
            dataIndex: 'setter',
            key: 'setter',
            render: (text, row) => (
              <div className="flex items-center space-x-2">
                {/* <img
                  // src={row?.user?.profilePicture}
                  alt={row?.user?.completeName}
                  className="w-8 h-8 rounded-full"
                /> */}
                <span>{row?.user?.completeName}</span>
              </div>
            ),
            sorter: (a, b) => a.user?.completeName.toLocaleLowerCase().localeCompare(b.user?.completeName.toLocaleLowerCase()),
          },          
          {
            title: t('leads'),
            dataIndex: 'leads',
            key: 'leads',
            render: (text, row) => (
              <div className="flex items-center space-x-2">
                <span>{thousand(row?.totalLeads)}</span>
              </div>
            ),
            sorter: (a, b) => a.totalLeads - b.totalLeads,
          },
          {
            title: t('conversion'),
            dataIndex: 'conversion',
            key: 'conversion',
            render: (text, row) => (
              <div className="flex items-center space-x-2">
                <span>{toCurrency(row?.historicalConversionAppointmentsHeld * 100, 2)}%</span>
              </div>
            ),
            sorter: (a, b) => a.historicalConversionAppointmentsHeld - b.historicalConversionAppointmentsHeld,
          },
          {
            title: t('referrals_by_customer'),
            dataIndex: 'referralsbycustomer',
            key: 'referralsbycustomer',
            render: (text, row) => (
              <div className="flex items-center space-x-2">
                <span>{thousand(row?.totalReferralsByCustomer)}</span>
              </div>
            ),
            sorter: (a, b) => a.totalReferralsByCustomer - b.totalReferralsByCustomer,
          },
        ]}
      />
    </div>
  );
}

export default OrgSettersSummaryTable;
